import { cn, Div, useTranslations } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  ComponentType,
  createElement,
  ElementType,
  memo,
  RefAttributes,
  SVGProps,
  useMemo,
} from 'react';

import { MessageCircleFilled16Icon } from '../icons';
import NewFontSvg from './new-font.svg?react';
import LinearGradient from '../LinearGradient';

export type CountTagProps<Component extends ElementType = typeof Div> =
  ComponentPropsWithoutRef<Component> &
    RefAttributes<ComponentRef<Component>> & {
      component?: Component;
      count: number;
      onImage?: boolean;
      isNew?: boolean;
      Icon?: ComponentType<SVGProps<SVGSVGElement>>;
    };

const CountTag = <Component extends ElementType = typeof Div>({
  component,
  count,
  className,
  Icon = MessageCircleFilled16Icon,
  onImage,
  isNew,
  ...props
}: CountTagProps<Component>) => {
  const t = useTranslations('단위.숫자');
  const label = useMemo(() => {
    if (count >= 10000000000) {
      return `${(count / 100000000).toFixed(0)}${t('억')}`;
    }
    if (count >= 100000000) {
      return `${(count / 100000000).toFixed(1)}${t('억')}`;
    }
    if (count >= 1000000) {
      return `${(count / 10000).toFixed(0)}${t('만')}`;
    }
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)}${t('만')}`;
    }
    return String(count);
  }, [count, t]);

  return createElement(
    component ?? Div,
    {
      ...props,
      className: cn(
        'body12 flex shrink-0 flex-row gap-0.5 self-center rounded-[6px] px-1 font-medium',
        onImage
          ? 'bg-[#3E3E41]/[84%] py-0.5 text-white/70'
          : 'border border-white/[8%] bg-black/60 py-[3px] text-white/50',
        className,
      ),
    },
    isNew && (
      <LinearGradient className="-my-0.5 -ml-1 flex size-[18px] items-center justify-center rounded-l-[6px] bg-gradient-to-r from-[#6728FF] to-[#8728FF]">
        <NewFontSvg className="size-3" />
      </LinearGradient>
    ),
    <Icon
      className={cn(
        'self-center',
        onImage ? 'size-[14px] text-white/70' : 'size-[13px] text-white/50',
      )}
    />,
    label,
  );
};

export default memo(CountTag) as typeof CountTag;
